import { useState, useEffect, useCallback, useRef } from 'react';
import DOMPurify from 'dompurify';
import { api } from '../lib/api';
import { stripChapterPrefix } from '../lib/utils';
import { saveLocalProgress } from '../components/ContinueReading';

interface ReaderPageProps {
  chapterId: number;
  storySlug?: string;
  chapterNum?: number;
  resumeScroll?: number;
  onNavigate: (page: string, data?: any) => void;
  user: any;
  onTitleReady?: (title: string) => void;
}

type ReaderTheme = 'dark' | 'light' | 'sepia';

export default function ReaderPage({ chapterId: initialChapterId, storySlug, chapterNum, resumeScroll, onNavigate, user, onTitleReady }: ReaderPageProps) {
  const [chapter, setChapter] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [resolvedChapterId, setResolvedChapterId] = useState(initialChapterId);
  const [theme, setTheme] = useState<ReaderTheme>('dark');
  const [fontSize, setFontSize] = useState(18);
  const [fontFamily, setFontFamily] = useState<'serif' | 'sans'>('serif');
  const [showSettings, setShowSettings] = useState(false);
  const [commentParagraph, setCommentParagraph] = useState<number | null>(null);
  const [commentBody, setCommentBody] = useState('');
  const [isBookmarked, setIsBookmarked] = useState(false);
  const [showTipModal, setShowTipModal] = useState(false);
  const [tipAmount, setTipAmount] = useState<number | null>(null);
  const [tipping, setTipping] = useState(false);
  const [tipSuccess, setTipSuccess] = useState('');
  const [tipError, setTipError] = useState('');
  const scrollTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastSavedScrollRef = useRef<number>(0);
  const [scrollProgress, setScrollProgress] = useState(0);

  // Track scroll progress through the current chapter
  useEffect(() => {
    const handleScrollProgress = () => {
      const scrollTop = window.scrollY;
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      const pct = docHeight > 0 ? Math.min(100, Math.round((scrollTop / docHeight) * 100)) : 0;
      setScrollProgress(pct);
    };
    window.addEventListener('scroll', handleScrollProgress, { passive: true });
    handleScrollProgress();
    return () => window.removeEventListener('scroll', handleScrollProgress);
  }, [chapter]);

  useEffect(() => {
    setLoading(true);

    const fetchChapter = async () => {
      try {
        let chapId = initialChapterId;
        if (storySlug && chapterNum && !initialChapterId) {
          const story = await api.stories.getBySlug(storySlug);
          const ch = story.chapters?.find((c: any) => c.chapterNum === chapterNum);
          if (ch) chapId = ch.id;
          else throw new Error('Chapter not found');
        }
        setResolvedChapterId(chapId);
        const data = await api.chapters.get(chapId);
        setChapter(data);
        setIsBookmarked(data.isBookmarked);
        if (onTitleReady) {
          onTitleReady(`Chapter ${data.chapterNum}: ${stripChapterPrefix(data.title)} — ${data.story.title} — ScribeHaven`);
        }
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchChapter();
  }, [initialChapterId, storySlug, chapterNum]);

  const scrollRestoredRef = useRef(false);
  useEffect(() => {
    if (!chapter || loading || scrollRestoredRef.current) return;
    if (resumeScroll && resumeScroll > 0) {
      scrollRestoredRef.current = true;
      requestAnimationFrame(() => {
        window.scrollTo({ top: resumeScroll, behavior: 'instant' as ScrollBehavior });
      });
    }
  }, [chapter, loading]);

  useEffect(() => {
    if (!chapter) return;

    const saveProgress = (scrollY: number) => {
      const storyId = chapter.story?.id;
      if (!storyId) return;

      saveLocalProgress({
        storyId,
        storyTitle: chapter.story.title,
        storySlug: chapter.story.slug || storySlug,
        storyCover: chapter.story.coverUrl,
        storyGenre: chapter.story.genre,
        authorName: chapter.story.penName?.penName || chapter.story.author?.username || '',
        totalChapters: chapter.story._count?.chapters || chapter.story.totalChapters || 1,
        chapterId: resolvedChapterId,
        chapterTitle: chapter.title,
        chapterNum: chapter.chapterNum,
        scrollPosition: scrollY,
        percentComplete: 0,
      });

      if (user) {
        api.progress.save({
          storyId,
          chapterId: resolvedChapterId,
          scrollPosition: scrollY,
        }).catch(() => {});
      }
    };

    const handleScroll = () => {
      if (scrollTimerRef.current) return;
      scrollTimerRef.current = setTimeout(() => {
        scrollTimerRef.current = null;
        const scrollY = window.scrollY;
        if (Math.abs(scrollY - lastSavedScrollRef.current) > 50) {
          lastSavedScrollRef.current = scrollY;
          saveProgress(scrollY);
        }
      }, 5000);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (scrollTimerRef.current) {
        clearTimeout(scrollTimerRef.current);
        scrollTimerRef.current = null;
      }
      saveProgress(window.scrollY);
    };
  }, [chapter, resolvedChapterId, user, storySlug]);

  const handleComplete = useCallback(async () => {
    try {
      await api.chapters.complete(resolvedChapterId);
    } catch (err) {
      console.error('Complete failed:', err);
    }
  }, [resolvedChapterId]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting && entry.target.id === 'chapter-end') {
            handleComplete();
          }
        });
      },
      { threshold: 1.0 }
    );
    const el = document.getElementById('chapter-end');
    if (el) observer.observe(el);
    return () => observer.disconnect();
  }, [chapter, handleComplete]);

  const handleComment = async () => {
    if (!user || commentParagraph === null || !commentBody.trim()) return;
    try {
      const comment = await api.comments.create({
        chapterId: resolvedChapterId, paragraphIndex: commentParagraph, body: commentBody,
      });
      setChapter((c: any) => ({
        ...c,
        paragraphComments: [...c.paragraphComments, comment]
      }));
      setCommentBody('');
      setCommentParagraph(null);
    } catch (err) {
      console.error('Comment failed:', err);
    }
  };

  const handleBookmark = async () => {
    if (!user) return onNavigate('login');
    try {
      const res = await api.chapters.bookmark(resolvedChapterId);
      setIsBookmarked(res.bookmarked);
    } catch (err) {
      console.error('Bookmark failed:', err);
    }
  };

  const handleTip = async () => {
    if (!user || !tipAmount || !chapter) return;
    setTipping(true);
    setTipError('');
    setTipSuccess('');
    try {
      const result = await api.ink.tip(chapter.story.id, tipAmount);
      setTipSuccess(`Sent ${tipAmount} Ink! Your balance: ${result.newBalance}`);
      setTipAmount(null);
      setTimeout(() => { setShowTipModal(false); setTipSuccess(''); }, 2000);
    } catch (err: any) {
      setTipError(err.message || 'Failed to send tip');
    } finally {
      setTipping(false);
    }
  };

  const getSlug = () => chapter?.story?.slug || storySlug;

  const navigateToChapter = (chapId: number, chapNum?: number) => {
    onNavigate('reader', { chapterId: chapId, storySlug: getSlug(), chapterNum: chapNum });
  };

  // Keyboard navigation: left/right arrow keys to go between chapters
  useEffect(() => {
    if (!chapter) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      // Don't trigger when user is typing in an input, textarea, or contenteditable
      const tag = (e.target as HTMLElement).tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || (e.target as HTMLElement).isContentEditable) return;
      if (e.key === 'ArrowRight' && chapter.nextChapter) {
        navigateToChapter(chapter.nextChapter.id, chapter.nextChapter.chapterNum);
      } else if (e.key === 'ArrowLeft' && chapter.prevChapter) {
        navigateToChapter(chapter.prevChapter.id, chapter.prevChapter.chapterNum);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [chapter]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-32">
        <div className="w-10 h-10 border-3 border-ink-400 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!chapter) return <div className="text-center py-32 text-gray-400">Chapter not found</div>;

  const isHtml = /<[a-z][\s\S]*>/i.test(chapter.content);
  const paragraphs = isHtml
    ? [chapter.content]
    : chapter.content.split('\n').filter((p: string) => p.trim());
  const themeClasses: Record<ReaderTheme, string> = {
    dark: 'bg-gray-950 text-gray-200',
    light: 'bg-white text-gray-900',
    sepia: 'bg-amber-50 text-amber-950',
  };

  const progress = chapter.allChapters
    ? ((chapter.allChapters.findIndex((c: any) => c.id === resolvedChapterId) + 1) / chapter.allChapters.length) * 100
    : 0;

  return (
    <div className={`min-h-screen ${themeClasses[theme]} transition-colors`}>
      {/* Dual progress bar: top strip = chapter position in story, bottom strip = scroll progress in this chapter */}
      <div className="fixed top-0 left-0 right-0 z-50 flex flex-col">
        <div className="h-0.5 bg-gray-800/60">
          <div className="h-full bg-ink-700 transition-all duration-300" style={{ width: `${progress}%` }} title={`Story progress: chapter ${chapter.allChapters?.findIndex((c: any) => c.id === resolvedChapterId) + 1} of ${chapter.allChapters?.length}`} />
        </div>
        <div className="h-1 bg-gray-800">
          <div className="h-full bg-ink-500 transition-all duration-150" style={{ width: `${scrollProgress}%` }} />
        </div>
      </div>

      <div className="sticky top-1 z-40 backdrop-blur py-2">
        <div className="max-w-3xl mx-auto px-4 flex items-center justify-between">
          <button
            onClick={() => onNavigate('story', { id: chapter.story.id, slug: getSlug() })}
            className="text-sm opacity-70 hover:opacity-100"
          >
            ← {chapter.story.title}
          </button>
          <div className="flex items-center gap-2">
            <select
              value={resolvedChapterId}
              onChange={e => {
                const selectedId = Number(e.target.value);
                const selectedCh = chapter.allChapters?.find((ch: any) => ch.id === selectedId);
                navigateToChapter(selectedId, selectedCh?.chapterNum);
              }}
              className="bg-transparent text-sm border border-gray-700 rounded px-2 py-1 focus:outline-none"
            >
              {chapter.allChapters?.map((ch: any) => (
                <option key={ch.id} value={ch.id} className="bg-gray-900">
                  Ch. {ch.chapterNum}: {stripChapterPrefix(ch.title)}
                </option>
              ))}
            </select>
            <button
              onClick={handleBookmark}
              className={`text-lg ${isBookmarked ? 'text-amber-400' : 'opacity-50 hover:opacity-100'}`}
              title={isBookmarked ? 'Bookmarked' : 'Bookmark this chapter'}
            >
              🔖
            </button>
            <button
              onClick={() => {
                const url = `https://scribehaven.com/story/${chapter.story.slug || chapter.story.id}`;
                navigator.clipboard.writeText(url).then(() => {
                  // brief visual feedback via title
                });
                const text = `Reading "${chapter.story.title}" on ScribeHaven!`;
                if (navigator.share) {
                  navigator.share({ title: chapter.story.title, text, url }).catch(() => {});
                } else {
                  window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`, '_blank');
                }
              }}
              className="text-sm opacity-70 hover:opacity-100 p-1"
              title="Share this story"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
              </svg>
            </button>
            <button
              onClick={() => setShowSettings(!showSettings)}
              className="text-sm opacity-70 hover:opacity-100 p-1"
            >
              ⚙️
            </button>
          </div>
        </div>

        {showSettings && (
          <div className="max-w-3xl mx-auto px-4 mt-2">
            <div className={`${theme === 'dark' ? 'bg-gray-900 border-gray-800' : theme === 'sepia' ? 'bg-amber-100 border-amber-200' : 'bg-gray-100 border-gray-200'} border rounded-xl p-4 flex flex-wrap items-center gap-4`}>
              <div className="flex items-center gap-2">
                <span className="text-xs opacity-70">Size:</span>
                <button onClick={() => setFontSize(f => Math.max(14, f - 2))} className="w-7 h-7 rounded bg-gray-700/50 text-xs">A-</button>
                <span className="text-xs w-6 text-center">{fontSize}</span>
                <button onClick={() => setFontSize(f => Math.min(28, f + 2))} className="w-7 h-7 rounded bg-gray-700/50 text-xs">A+</button>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs opacity-70">Font:</span>
                <button onClick={() => setFontFamily('serif')} className={`px-2 py-1 rounded text-xs ${fontFamily === 'serif' ? 'bg-ink-600' : 'bg-gray-700/50'}`}>Serif</button>
                <button onClick={() => setFontFamily('sans')} className={`px-2 py-1 rounded text-xs ${fontFamily === 'sans' ? 'bg-ink-600' : 'bg-gray-700/50'}`}>Sans</button>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs opacity-70">Theme:</span>
                <button onClick={() => setTheme('dark')} className={`w-7 h-7 rounded-full bg-gray-900 border-2 ${theme === 'dark' ? 'border-ink-500' : 'border-transparent'}`} />
                <button onClick={() => setTheme('light')} className={`w-7 h-7 rounded-full bg-white border-2 ${theme === 'light' ? 'border-ink-500' : 'border-transparent'}`} />
                <button onClick={() => setTheme('sepia')} className={`w-7 h-7 rounded-full bg-amber-100 border-2 ${theme === 'sepia' ? 'border-ink-500' : 'border-transparent'}`} />
              </div>
            </div>
          </div>
        )}
      </div>

      <article className="max-w-3xl mx-auto px-4 py-12">
        <header className="text-center mb-12">
          <span className="text-sm opacity-50">Chapter {chapter.chapterNum}</span>
          <h1 className="text-3xl font-bold mt-1">{stripChapterPrefix(chapter.title)}</h1>
          <div className="flex items-center justify-center gap-3 mt-2">
            <span className="text-sm opacity-50">{chapter.wordCount.toLocaleString()} words</span>
            <span className="text-sm opacity-30">·</span>
            <span className="text-sm opacity-50">~{Math.max(1, Math.ceil(chapter.wordCount / 250))} min read</span>
            {scrollProgress > 0 && scrollProgress < 100 && (
              <>
                <span className="text-sm opacity-30">·</span>
                <span className="text-sm opacity-50">{scrollProgress}% through chapter</span>
              </>
            )}
          </div>
        </header>

        <div
          className={fontFamily === 'serif' ? 'reading-view' : ''}
          style={{ fontSize: `${fontSize}px`, lineHeight: '1.8' }}
        >
          {isHtml ? (
            <div
              className="prose prose-invert max-w-none
                [&_h2]:text-2xl [&_h2]:font-bold [&_h2]:mt-8 [&_h2]:mb-4
                [&_h3]:text-xl [&_h3]:font-semibold [&_h3]:mt-6 [&_h3]:mb-3
                [&_blockquote]:border-l-4 [&_blockquote]:border-ink-500 [&_blockquote]:pl-4 [&_blockquote]:italic [&_blockquote]:opacity-80 [&_blockquote]:my-4
                [&_hr]:border-gray-700 [&_hr]:my-8
                [&_ul]:list-disc [&_ul]:pl-6 [&_ol]:list-decimal [&_ol]:pl-6
                [&_p]:mb-4 [&_p]:leading-relaxed"
              dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(chapter.content, { ALLOWED_TAGS: ['p', 'br', 'b', 'i', 'u', 's', 'em', 'strong', 'h2', 'h3', 'blockquote', 'ul', 'ol', 'li', 'hr', 'div', 'span'], ALLOWED_ATTR: ['style', 'class'] }) }}
            />
          ) : (
            paragraphs.map((p: string, i: number) => {
              const comments = chapter.paragraphComments?.filter((c: any) => c.paragraphIndex === i) || [];
              return (
                <div key={i} className="group relative mb-6">
                  <p
                    className="cursor-text"
                    onClick={() => user && setCommentParagraph(commentParagraph === i ? null : i)}
                  >
                    {p}
                  </p>
                  {comments.length > 0 && (
                    <div className="ml-4 mt-2 space-y-1">
                      {comments.map((c: any) => (
                        <div key={c.id} className="text-xs px-3 py-1.5 rounded-lg bg-ink-600/10 border border-ink-500/20 text-ink-300">
                          <span className="font-medium">{c.user?.username}:</span> {c.body}
                        </div>
                      ))}
                    </div>
                  )}
                  {commentParagraph === i && (
                    <div className="mt-2 flex gap-2">
                      <input
                        type="text"
                        value={commentBody}
                        onChange={e => setCommentBody(e.target.value)}
                        placeholder="Add a comment on this paragraph..."
                        className="flex-1 bg-gray-800/50 border border-gray-700 rounded-lg px-3 py-1.5 text-sm focus:border-ink-500 focus:outline-none"
                        onKeyDown={e => e.key === 'Enter' && handleComment()}
                      />
                      <button
                        onClick={handleComment}
                        className="px-3 py-1.5 bg-ink-600 rounded-lg text-sm"
                      >
                        Post
                      </button>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>

        {chapter.authorNote && (
          <div className={`mt-12 p-4 rounded-xl border ${theme === 'dark' ? 'bg-gray-900 border-gray-800' : theme === 'sepia' ? 'bg-amber-100 border-amber-200' : 'bg-gray-50 border-gray-200'}`}>
            <h3 className="text-sm font-semibold mb-2 opacity-70">Author's Note</h3>
            <p className="text-sm opacity-80">{chapter.authorNote}</p>
          </div>
        )}

        <div id="chapter-end" />

        {user && chapter.story.authorId !== user.id && (
          <div className="mt-10 flex justify-center">
            <button
              onClick={() => { setShowTipModal(!showTipModal); setTipError(''); setTipSuccess(''); }}
              className="px-6 py-3 bg-gradient-to-r from-amber-600 to-amber-500 hover:from-amber-500 hover:to-amber-400 rounded-xl font-semibold text-white transition-all flex items-center gap-2 shadow-lg"
            >
              <span className="text-lg">✦</span> Give Ink to Author
            </button>
          </div>
        )}

        {showTipModal && (
          <div className="mt-4 max-w-sm mx-auto p-5 rounded-xl border border-amber-800/50 bg-gray-900/80 backdrop-blur">
            <p className="text-center text-sm text-gray-300 mb-4">
              Show appreciation to <strong className="text-amber-400">{chapter.story.author?.username || 'the author'}</strong>
            </p>
            <div className="flex justify-center gap-3 mb-4">
              {[10, 50, 100].map(amt => (
                <button
                  key={amt}
                  onClick={() => setTipAmount(amt)}
                  className={`px-5 py-2.5 rounded-lg font-semibold text-sm transition-all ${
                    tipAmount === amt
                      ? 'bg-amber-600 text-white ring-2 ring-amber-400 scale-105'
                      : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                  }`}
                >
                  {amt} ✦
                </button>
              ))}
            </div>
            {tipError && <p className="text-red-400 text-sm text-center mb-3">{tipError}</p>}
            {tipSuccess && <p className="text-emerald-400 text-sm text-center mb-3">{tipSuccess}</p>}
            <button
              onClick={handleTip}
              disabled={!tipAmount || tipping}
              className="w-full py-2.5 bg-amber-600 hover:bg-amber-500 disabled:bg-gray-700 disabled:text-gray-500 rounded-lg font-semibold transition-colors"
            >
              {tipping ? 'Sending...' : tipAmount ? `Send ${tipAmount} Ink` : 'Select an amount'}
            </button>
          </div>
        )}

        <nav className="flex items-center justify-between mt-12 pt-8 border-t border-gray-800">
          {chapter.prevChapter ? (
            <button
              onClick={() => navigateToChapter(chapter.prevChapter.id, chapter.prevChapter.chapterNum)}
              className="px-5 py-2.5 bg-gray-800 hover:bg-gray-700 rounded-lg font-medium transition-colors"
            >
              ← Previous
            </button>
          ) : <div />}
          {chapter.nextChapter ? (
            <button
              onClick={() => navigateToChapter(chapter.nextChapter.id, chapter.nextChapter.chapterNum)}
              className="px-5 py-2.5 bg-ink-600 hover:bg-ink-500 rounded-lg font-medium transition-colors"
            >
              Next Chapter →
            </button>
          ) : (
            <button
              onClick={() => onNavigate('story', { id: chapter.story.id, slug: getSlug() })}
              className="px-5 py-2.5 bg-gray-800 hover:bg-gray-700 rounded-lg font-medium transition-colors"
            >
              Back to Story
            </button>
          )}
        </nav>
        <p className="text-center text-xs text-gray-600 mt-3 select-none">
          Tip: Use ← → arrow keys to navigate chapters
        </p>

        <DiscussionSection
          chapterId={resolvedChapterId}
          user={user}
          theme={theme}
          onNavigate={onNavigate}
        />
      </article>
    </div>
  );
}

function timeAgo(dateStr: string): string {
  const now = Date.now();
  const then = new Date(dateStr).getTime();
  const seconds = Math.floor((now - then) / 1000);
  if (seconds < 60) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  const months = Math.floor(days / 30);
  if (months < 12) return `${months}mo ago`;
  return `${Math.floor(months / 12)}y ago`;
}

function DiscussionSection({ chapterId, user, theme, onNavigate }: { chapterId: number; user: any; theme: ReaderTheme; onNavigate: (page: string, data?: any) => void }) {
  const [comments, setComments] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [sort, setSort] = useState<'newest' | 'oldest'>('newest');
  const [body, setBody] = useState('');
  const [posting, setPosting] = useState(false);
  const [replyingTo, setReplyingTo] = useState<number | null>(null);
  const [replyBody, setReplyBody] = useState('');
  const [replyPosting, setReplyPosting] = useState(false);
  const MAX_CHARS = 2000;

  const themeBorder = theme === 'dark' ? 'border-gray-800' : theme === 'sepia' ? 'border-amber-200' : 'border-gray-200';
  const themeBg = theme === 'dark' ? 'bg-gray-900' : theme === 'sepia' ? 'bg-amber-100' : 'bg-gray-100';
  const themeInputBg = theme === 'dark' ? 'bg-gray-800 border-gray-700 text-gray-200 placeholder-gray-500' : theme === 'sepia' ? 'bg-amber-50 border-amber-300 text-amber-950 placeholder-amber-400' : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400';
  const themeSubText = theme === 'dark' ? 'text-gray-400' : theme === 'sepia' ? 'text-amber-700' : 'text-gray-500';
  const themeBtnActive = theme === 'dark' ? 'bg-ink-600 text-white' : theme === 'sepia' ? 'bg-amber-600 text-white' : 'bg-ink-600 text-white';
  const themeBtnInactive = theme === 'dark' ? 'bg-gray-800 text-gray-400 hover:bg-gray-700' : theme === 'sepia' ? 'bg-amber-200 text-amber-700 hover:bg-amber-300' : 'bg-gray-200 text-gray-600 hover:bg-gray-300';

  const fetchComments = useCallback(async () => {
    try {
      const data = await api.chapterComments.list(chapterId, { sort });
      setComments(data.comments || []);
      setTotal(data.totalWithReplies ?? data.total ?? 0);
    } catch (err) {
      console.error('Failed to load comments:', err);
    }
  }, [chapterId, sort]);

  useEffect(() => { fetchComments(); }, [fetchComments]);

  const handlePost = async () => {
    if (!body.trim() || body.length > MAX_CHARS || posting) return;
    setPosting(true);
    try {
      const comment = await api.chapterComments.create({ chapterId, body: body.trim() });
      if (sort === 'newest') {
        setComments(prev => [comment, ...prev]);
      } else {
        setComments(prev => [...prev, comment]);
      }
      setTotal(t => t + 1);
      setBody('');
    } catch (err) {
      console.error('Failed to post comment:', err);
    } finally {
      setPosting(false);
    }
  };

  const handleReply = async (parentId: number) => {
    if (!replyBody.trim() || replyBody.length > MAX_CHARS || replyPosting) return;
    setReplyPosting(true);
    try {
      const reply = await api.chapterComments.create({ chapterId, body: replyBody.trim(), parentId });
      setComments(prev => prev.map(c => c.id === parentId ? { ...c, replies: [...(c.replies || []), reply] } : c));
      setTotal(t => t + 1);
      setReplyBody('');
      setReplyingTo(null);
    } catch (err) {
      console.error('Failed to post reply:', err);
    } finally {
      setReplyPosting(false);
    }
  };

  const handleDelete = async (commentId: number, parentId?: number) => {
    if (!confirm('Delete this comment?')) return;
    try {
      await api.chapterComments.delete(commentId);
      if (parentId) {
        setComments(prev => prev.map(c => c.id === parentId ? { ...c, replies: (c.replies || []).filter((r: any) => r.id !== commentId) } : c));
      } else {
        setComments(prev => prev.filter(c => c.id !== commentId));
      }
      setTotal(t => Math.max(0, t - 1));
    } catch (err) {
      console.error('Failed to delete comment:', err);
    }
  };

  const renderAvatar = (u: any) => {
    if (u?.avatarUrl) {
      return <img src={u.avatarUrl} alt="" className="w-8 h-8 rounded-full object-cover" />;
    }
    const initial = (u?.username || '?')[0].toUpperCase();
    return (
      <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${theme === 'dark' ? 'bg-ink-600/30 text-ink-300' : theme === 'sepia' ? 'bg-amber-300 text-amber-800' : 'bg-ink-100 text-ink-600'}`}>
        {initial}
      </div>
    );
  };

  const renderComment = (comment: any, isReply = false, parentId?: number) => (
    <div key={comment.id} className={`flex gap-3 ${isReply ? `ml-8 pl-4 border-l-2 ${themeBorder}` : ''}`}>
      <div className="flex-shrink-0 pt-1">
        {renderAvatar(comment.user)}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={() => onNavigate('profile', { username: comment.user?.username })}
            className="font-semibold text-sm hover:underline"
          >
            {comment.user?.username || 'Unknown'}
          </button>
          <span className={`text-xs ${themeSubText}`}>{timeAgo(comment.createdAt)}</span>
        </div>
        <p className="text-sm mt-1 whitespace-pre-wrap break-words">{comment.body}</p>
        <div className="flex items-center gap-3 mt-1.5">
          {!isReply && user && (
            <button
              onClick={() => { setReplyingTo(replyingTo === comment.id ? null : comment.id); setReplyBody(''); }}
              className={`text-xs ${themeSubText} hover:opacity-80`}
            >
              Reply
            </button>
          )}
          {user && comment.userId === user.id && (
            <button
              onClick={() => handleDelete(comment.id, parentId)}
              className="text-xs text-red-400 hover:text-red-300"
              title="Delete"
            >
              🗑
            </button>
          )}
        </div>

        {replyingTo === comment.id && (
          <div className="mt-2">
            <textarea
              value={replyBody}
              onChange={e => setReplyBody(e.target.value.slice(0, MAX_CHARS))}
              placeholder="Write a reply..."
              rows={2}
              className={`w-full rounded-lg px-3 py-2 text-sm border focus:outline-none focus:ring-1 focus:ring-ink-500 resize-none ${themeInputBg}`}
            />
            <div className="flex items-center justify-between mt-1">
              <span className={`text-xs ${themeSubText}`}>{replyBody.length}/{MAX_CHARS}</span>
              <div className="flex gap-2">
                <button onClick={() => setReplyingTo(null)} className={`text-xs px-3 py-1 rounded ${themeBtnInactive}`}>Cancel</button>
                <button
                  onClick={() => handleReply(comment.id)}
                  disabled={!replyBody.trim() || replyPosting}
                  className={`text-xs px-3 py-1.5 rounded font-medium transition-colors ${!replyBody.trim() || replyPosting ? 'opacity-50 cursor-not-allowed' : ''} ${themeBtnActive}`}
                >
                  {replyPosting ? 'Posting...' : 'Reply'}
                </button>
              </div>
            </div>
          </div>
        )}

        {!isReply && comment.replies?.length > 0 && (
          <div className="mt-3 space-y-3">
            {comment.replies.map((r: any) => renderComment(r, true, comment.id))}
          </div>
        )}
      </div>
    </div>
  );

  return (
    <section className={`mt-12 pt-8 border-t ${themeBorder}`}>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-bold">Discussion ({total})</h2>
        <div className="flex gap-1">
          <button
            onClick={() => setSort('newest')}
            className={`text-xs px-3 py-1.5 rounded-l-lg font-medium transition-colors ${sort === 'newest' ? themeBtnActive : themeBtnInactive}`}
          >
            Newest
          </button>
          <button
            onClick={() => setSort('oldest')}
            className={`text-xs px-3 py-1.5 rounded-r-lg font-medium transition-colors ${sort === 'oldest' ? themeBtnActive : themeBtnInactive}`}
          >
            Oldest
          </button>
        </div>
      </div>

      {user ? (
        <div className="mb-6">
          <textarea
            value={body}
            onChange={e => setBody(e.target.value.slice(0, MAX_CHARS))}
            placeholder="Share your thoughts on this chapter..."
            rows={3}
            className={`w-full rounded-lg px-4 py-3 text-sm border focus:outline-none focus:ring-1 focus:ring-ink-500 resize-none ${themeInputBg}`}
          />
          <div className="flex items-center justify-between mt-2">
            <span className={`text-xs ${themeSubText}`}>{body.length}/{MAX_CHARS}</span>
            <button
              onClick={handlePost}
              disabled={!body.trim() || posting}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${!body.trim() || posting ? 'opacity-50 cursor-not-allowed' : ''} ${themeBtnActive}`}
            >
              {posting ? 'Posting...' : 'Post Comment'}
            </button>
          </div>
        </div>
      ) : (
        <div className={`mb-6 p-4 rounded-lg text-center text-sm ${themeBg} ${themeBorder} border`}>
          <button onClick={() => onNavigate('login')} className="text-ink-400 hover:text-ink-300 font-medium">
            Sign in to join the discussion
          </button>
        </div>
      )}

      {comments.length === 0 ? (
        <p className={`text-center py-8 text-sm ${themeSubText}`}>
          No comments yet. Be the first to share your thoughts!
        </p>
      ) : (
        <div className="space-y-5">
          {comments.map(c => renderComment(c))}
        </div>
      )}
    </section>
  );
}
